import { useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../../utils/PMDialog";
import { PMField } from "../../../utils/PMField";
import { DocumentManagerV6 } from "../../Media/DocumentManagerV6";
import { DocumentViewer } from "../../Media/documentViewer";
import PMCombobox from "../../../utils/PMCombobox";
import {
  createDeviationItem,
  updateDeviationItem,
} from "../../../../api/quality/deviation";
import { deviationConsequenceOptions } from "./utils";

const DIALOG_CONFIG = {
  explanation: {
    title: "Legg til eller rediger forklaring",
    itemTitle: "EXPLANATION",
    submitLabel: "Lagre forklaring",
  },
  consequence: {
    title: "Legg til konsekvens",
    itemTitle: "CONSEQUENCE",
    submitLabel: "Lagre konsekvens",
  },
};

export const DeviationItemEditorDialog = ({
  open,
  onClose,
  deviation,
  item,
  kind = "explanation",
  fixedConsequence = null,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState(item?.documents || []);
  const config = DIALOG_CONFIG[kind] || DIALOG_CONFIG.explanation;
  const lockedConsequence = fixedConsequence ? String(fixedConsequence) : "";
  const dialogTitle = lockedConsequence
    ? t("deviation.consequence.dialog_title", {
        type: t(`deviation.consequence.type.${lockedConsequence}`),
      })
    : config.title;

  const validationSchema =
    kind === "consequence"
      ? Yup.object({
          description: Yup.string().required(t("deviation.form.description_required")),
          consequence: Yup.string().required(t("deviation.form.consequence_required")),
        })
      : Yup.object({
          description: Yup.string().required(t("deviation.form.description_required")),
        });

  const handleUploadCallback = (nextDocuments, formik) => {
    const docs = Array.isArray(nextDocuments) ? nextDocuments : [];
    setDocuments(docs);
    formik.setFieldValue("documents", docs);
  };

  return (
    <PMDialog open={open} onClose={onClose} title={dialogTitle} size="3xl">
      <Formik
        enableReinitialize
        initialValues={{
          id: item?.id || null,
          deviation_id: deviation?.id,
          title: config.itemTitle,
          description: item?.description || "",
          consequence: lockedConsequence || (item?.consequence ? String(item.consequence) : ""),
          documents: item?.documents || [],
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, helpers) => {
          try {
            const action = values.id ? updateDeviationItem : createDeviationItem;
            const response = await action(values);
            const savedItem = response?.data;

            if (!savedItem?.id) {
              throw new Error("Kunne ikke lagre avvikspunktet");
            }

            toast.success(config.submitLabel);
            onSaved?.(savedItem, kind);
            helpers.setSubmitting(false);
            onClose?.();
          } catch (error) {
            console.error(error);
            toast.error(t("deviation.toast.save_failed"));
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <Form className="space-y-6">
            {kind === "consequence" && !lockedConsequence && (
              <div>
                <PMCombobox
                  name="consequence"
                  label={t("deviation.item.consequence")}
                  options={deviationConsequenceOptions}
                  value={formik.values.consequence}
                  {...formik}
                />
              </div>
            )}

            <div>
              <PMField
                name="description"
                label={kind === "explanation" ? "Forklaring" : "Beskrivelse"}
                multiline
                rows={6}
                {...formik}
              />
            </div>

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">
                    Bilder og vedlegg
                  </h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Last opp bilder eller dokumentasjon som hører til denne posten.
                  </p>
                </div>
                {deviation?.id ? (
                  <DocumentManagerV6
                    module="deviation"
                    moduleParam={deviation.id}
                    mode="inline"
                    allowCopy={false}
                    allowMove={false}
                    onDocumentsChange={(data) => handleUploadCallback(data, formik)}
                  />
                ) : (
                  <p className="text-sm text-slate-500">{t("hse.upload.save_first")}</p>
                )}
              </div>

              <div className="mt-4">
                <DocumentViewer
                  pathType="url"
                  documents={formik.values.documents || documents}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={formik.submitForm}
                disabled={formik.isSubmitting}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {lockedConsequence ? t("deviation.consequence.save") : config.submitLabel}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </PMDialog>
  );
};
